import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';

export const useUnreadAudioCount = () => {
  const { user } = useAuth();
  const { isCoach } = useUserRole();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) return;

    const fetchUnreadCount = async () => {
      if (isCoach) {
        // Only count clients with active accounts and Active Audio access
        const { data: eligible } = await supabase
          .from('profiles')
          .select('id, package_type, status')
          .eq('status', 'active')
          .neq('package_type', 'essentials');

        const eligibleIds = new Set((eligible || []).map(p => p.id));

        // 1. Unread original messages FROM clients
        const { data: clientMessages } = await supabase
          .from('audio_messages')
          .select('id, created_by, user_id')
          .eq('read_by_coach', false);

        const clientUnreadCount = clientMessages?.filter(
          msg => msg.created_by === msg.user_id && msg.created_by !== user.id && eligibleIds.has(msg.user_id)
        ).length || 0;

        // 2. Unread client replies to coach-created messages
        const { data: clientReplies } = await supabase
          .from('audio_messages')
          .select('id, user_id')
          .eq('client_reply_read', false)
          .or('client_reply_text.not.is.null,client_reply_audio_url.not.is.null');

        const replyCount = (clientReplies || []).filter(m => eligibleIds.has(m.user_id)).length;

        setUnreadCount(clientUnreadCount + replyCount);
      } else {
        // Client sees:
        // 1. Unread coach replies (coach_reply_read = false and has reply)
        const { data: replyMessages } = await supabase
          .from('audio_messages')
          .select('id')
          .eq('user_id', user.id)
          .eq('coach_reply_read', false)
          .or('coach_reply_text.not.is.null,coach_reply_audio_url.not.is.null');
        
        // 2. Unread coach original messages (created_by != user_id, read_by_client = false)
        const { data: coachOriginalMessages } = await supabase
          .from('audio_messages')
          .select('id')
          .eq('user_id', user.id)
          .eq('read_by_client', false)
          .neq('created_by', user.id);
        
        const totalCount = (replyMessages?.length || 0) + (coachOriginalMessages?.length || 0);
        setUnreadCount(totalCount);
      }
    };

    fetchUnreadCount();

    // Subscribe to real-time changes
    const channel = supabase
      .channel(`audio_messages_unread_${user.id}_${Math.random().toString(36).slice(2, 10)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'audio_messages'
        },
        () => {
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, isCoach]);

  return unreadCount;
};
